/**
 * Customer org-membership RPC definitions (CLA-705).
 *
 * `customers.listForActor` backs the dashboard's multi-org picker: it returns
 * EVERY customer (org) the authenticated caller is a member of — resolved from
 * `CurrentActor` via the `customer_members` chain, never from client input — so
 * a person in more than one org can switch between them rather than being
 * silently collapsed to one. Actor-scoped under {@link AuthMiddleware} (NOT
 * `AdminMiddleware`): the caller sees only their own memberships.
 *
 * Each org carries its membership `role` (`customer_members.role`) and a `plan`
 * summary derived from the same subscription read the plan badge uses (null when
 * the org holds no subscription). No secret or cross-entity fields cross.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { CustomerMemberRole } from "@fm/db/models/CustomerMember";
import { SubscriptionPlan, SubscriptionStatus } from "@fm/db/models/Subscription";
import { Schema } from "effect";
import { AuthMiddleware } from "../auth.tags.ts";
import { DatabaseError, Unauthorized } from "../errors.ts";

/**
 * The plan summary for one org — plan cadence, status and amount from the org's
 * subscription (the same read behind the dashboard plan badge). Null on the
 * enclosing field when the org has no subscription.
 *
 * @since 1.0.0
 */
export const OrgPlanSummary = Schema.Struct({
  plan: SubscriptionPlan,
  status: SubscriptionStatus,
  amount: Schema.NullOr(Schema.Number),
});
export type OrgPlanSummary = Schema.Schema.Type<typeof OrgPlanSummary>;

/**
 * One org the authenticated caller is a member of: the customer id/name/slug,
 * the caller's membership `role`, and the org's `plan` summary (null when the
 * org holds no subscription). `slug` is null for a not-yet-provisioned customer.
 *
 * @since 1.0.0
 */
export const OrgMembershipSummary = Schema.Struct({
  customerId: Schema.String,
  name: Schema.String,
  slug: Schema.NullOr(Schema.String),
  role: CustomerMemberRole,
  plan: Schema.NullOr(OrgPlanSummary),
});
export type OrgMembershipSummary = Schema.Schema.Type<typeof OrgMembershipSummary>;

/**
 * Response for `customers.listForActor`: every org the caller belongs to,
 * ordered by name.
 *
 * @since 1.0.0
 */
export const OrgMembershipListResponse = Schema.Array(OrgMembershipSummary);
export type OrgMembershipListResponse = Schema.Schema.Type<typeof OrgMembershipListResponse>;

/**
 * Customer org-membership RPC group. `customers.listForActor` takes no payload:
 * the target is always the authenticated caller (resolved from `CurrentActor`).
 *
 * @since 1.0.0
 */
export class CustomerOrgsRpcs extends RpcGroup.make(
  Rpc.make("customers.listForActor", {
    success: OrgMembershipListResponse,
    error: Schema.Union(Unauthorized, DatabaseError),
  }).middleware(AuthMiddleware),
) {}
